import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Session, Component, Feature, Platform, TestPlan, UserRecord } from '../types';
import { InsightSection } from '../components/insights/PDFReportModal';
import { formatDate } from './date';
import { pdfSectionDescriptions as sectionDescriptions } from './pdfDescriptions';

interface PDFGenerationData {
  sessions: Session[];
  components: Component[];
  features: Feature[];
  testPlans: TestPlan[];
  users: UserRecord[];
  platforms: { id: string; name: string }[];
  selectedProduct: { id: string; name: string } | null;
  milestone: string;
  selectedSections: InsightSection[]; // Ordered array of sections to include
  // Computed data functions
  getPlatformTestingStats: () => any[];
  getUntestedComponents: () => any[];
  getComponentsNeedingTestCases: () => any[];
  getBugRatesPerPlatform: () => any[];
  getBugRatesPerSessionType: () => any[];
  getBugRatesPerFeature: () => any[];
  getComponentHealth: () => any[];
  getMostBugProneComponents: () => any[];
  getMostBugProneFeatures: () => any[];
  getBugDiscoveryRate: () => Array<{ date: string; bugs: number; buggedComponents: number }>;
  getTestingTrends: () => Array<{ date: string; platform: Platform; count: number }>;
  getTesterPerformance: () => Array<{ testerName: string; sessionsCompleted: number; sessionsCreated: number; completionRate: number; avgDuration?: number; bugsFound: number; componentsTested: number; activeSessions: number; primaryPlatform?: string }>;
  getMilestoneCompletion: () => Array<{ milestone: string; testPlanCompletion: number; sessionCompletion: number; overallCompletion: number; totalSessions: number; completedSessions: number }>;
  getPlatformMap: () => Record<string, Record<string, any>>;
  getTestPlanCompletion: () => Array<{ testPlan: TestPlan; completionRate: number; platformStatus: any[] }>;
  getFeatureCoverage: () => Array<{ feature: Feature; totalComponents: number; testedComponents: number; percentage: number }>;
  getBugTrackerData: () => Array<{ 
    feature: Feature; 
    uniqueBugUrls: number; 
    buggedComponentsCount: number; 
    components: Array<{ 
      component: Component; 
      bugs: Array<{ url: string; platforms: string[] }> 
    }> 
  }>;
}

// Helper to wrap text to fit within width
const wrapText = (text: string, maxWidth: number, doc: jsPDF): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    const testLine = currentLine + (currentLine ? ' ' : '') + word;
    const width = doc.getTextWidth(testLine);
    if (width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
};

// Helper to add a section title
const addSectionTitle = (doc: jsPDF, title: string, y: number): number => {
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0); // Ensure black text for all section titles
  doc.text(title, 14, y);
  return y + 8;
};

// Helper to add a subsection title
const addSubsectionTitle = (doc: jsPDF, title: string, y: number): number => {
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0); // Ensure black text
  doc.text(title, 20, y);
  return y + 6;
};

// Helper to add regular text
const addText = (doc: jsPDF, text: string, x: number, y: number, maxWidth?: number): number => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0); // Ensure black text
  if (maxWidth) {
    const lines = wrapText(text, maxWidth, doc);
    lines.forEach((line, idx) => {
      doc.text(line, x, y + (idx * 5));
    });
    return y + (lines.length * 5);
  } else {
    doc.text(text, x, y);
    return y + 5;
  }
};

// Helper to check if we need a new page
const checkPageBreak = (doc: jsPDF, y: number, requiredSpace: number = 20): number => {
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  if (y + requiredSpace > pageHeight - margin) {
    doc.addPage();
    return 20;
  }
  return y;
};

// Helper to add section description
const addSectionDescription = (doc: jsPDF, description: string | undefined, x: number, y: number, maxWidth: number): number => {
  // Skip if no description provided
  if (!description) return y;
  
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  
  // Split by paragraphs (double newline)
  const paragraphs = description.split('\n\n');
  
  paragraphs.forEach((paragraph, pIdx) => {
    // Check if paragraph starts with a key term (e.g., "Unique Bugs" or "Bugged Components")
    const keyTermMatch = paragraph.match(/^([A-Z][a-zA-Z\s]+)\s(represent|are)/);
    
    if (keyTermMatch) {
      // Render key term in bold
      const keyTerm = keyTermMatch[1];
      const restOfParagraph = paragraph.substring(keyTerm.length);
      
      doc.setFont('helvetica', 'bold');
      doc.text(keyTerm, x, y);
      const keyTermWidth = doc.getTextWidth(keyTerm);
      
      // Render rest of paragraph in italic
      doc.setFont('helvetica', 'italic');
      
      // Wrap the rest of the paragraph
      const lines = wrapText(restOfParagraph, maxWidth, doc);
      
      // First line continues after key term
      if (lines.length > 0) {
        const firstLine = lines[0].trim();
        if (firstLine.length > 0 && keyTermWidth + doc.getTextWidth(firstLine) <= maxWidth) {
          doc.text(firstLine, x + keyTermWidth, y);
          y += 4;
          // Remaining lines
          for (let i = 1; i < lines.length; i++) {
            doc.text(lines[i], x, y);
            y += 4;
          }
        } else {
          y += 4;
          lines.forEach((line) => {
            doc.text(line, x, y);
            y += 4;
          });
        }
      }
    } else {
      // Regular paragraph - just italic
      doc.setFont('helvetica', 'italic');
      const lines = wrapText(paragraph, maxWidth, doc);
      lines.forEach((line) => {
        doc.text(line, x, y);
        y += 4;
      });
    }
    
    // Add space between paragraphs
    if (pIdx < paragraphs.length - 1) {
      y += 3;
    }
  });
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  return y + 4;
};

// Helper to draw a card/box
const drawCard = (doc: jsPDF, x: number, y: number, width: number, height: number, title: string, value: string, isDanger: boolean = false): void => {
  // Draw card border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, width, height, 2, 2);
  
  // Fill background
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, width, height, 2, 2, 'FD');
  
  // Title
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(title, x + 5, y + 8);
  
  // Value
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  if (isDanger) {
    doc.setTextColor(220, 53, 69); // Red for danger
  } else {
    doc.setTextColor(0, 0, 0);
  }
  doc.text(value, x + 5, y + 18);
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
};

// Helper to draw a line chart
const drawLineChart = (doc: jsPDF, x: number, y: number, width: number, height: number, data: Array<{ date: string; value: number }>, label: string, color: number[] = [0, 0, 0]): number => {
  if (data.length === 0) return y;
  
  const padding = { top: 10, right: 10, bottom: 25, left: 30 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Find max value for scaling
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const yAxisMax = Math.ceil(maxValue * 1.1);
  
  // Draw chart border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.rect(x + padding.left, y + padding.top, chartWidth, chartHeight);
  
  // Draw grid lines
  const gridLines = 5;
  for (let i = 0; i <= gridLines; i++) {
    const gridY = y + padding.top + (i / gridLines) * chartHeight;
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.3);
    doc.line(x + padding.left, gridY, x + padding.left + chartWidth, gridY);
    
    // Y-axis labels (whole numbers only)
    const value = Math.round((yAxisMax / gridLines) * (gridLines - i));
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(value.toString(), x + padding.left - 5, gridY + 2, { align: 'right' });
  }
  
  // Calculate points for the line
  const points = data.map((d, idx) => {
    const pointX = x + padding.left + (idx / (data.length - 1 || 1)) * chartWidth;
    const pointY = y + padding.top + chartHeight - (d.value / yAxisMax) * chartHeight;
    return { x: pointX, y: pointY, value: d.value, date: d.date };
  });
  
  // Draw area fill FIRST (before line) - create proper area under the curve
  if (points.length > 0) {
    const bottomY = y + padding.top + chartHeight;
    
    // Create fill color (lighter version of line color)
    const fillColor = [
      Math.min(255, Math.max(0, color[0] + 180)),
      Math.min(255, Math.max(0, color[1] + 180)),
      Math.min(255, Math.max(0, color[2] + 180))
    ];
    doc.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
    
    // Draw area by creating small rectangles that follow the line exactly
    // Use many small segments to create a smooth fill that sticks to the line
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      
      if (p2.x > p1.x) {
        // Use many small rectangles to create a smooth fill
        const segmentWidth = p2.x - p1.x;
        const numSegments = Math.max(10, Math.floor(segmentWidth / 1)); // 1mm per segment
        
        for (let seg = 0; seg < numSegments; seg++) {
          const segStartX = p1.x + (seg / numSegments) * segmentWidth;
          const segEndX = p1.x + ((seg + 1) / numSegments) * segmentWidth;
          
          // Calculate Y at start and end of this segment using linear interpolation
          const segStartY = p1.y + ((p2.y - p1.y) * (seg / numSegments));
          const segEndY = p1.y + ((p2.y - p1.y) * ((seg + 1) / numSegments));
          
          // Use the minimum Y to ensure we fill from the line down
          const segTopY = Math.min(segStartY, segEndY);
          const segHeight = bottomY - segTopY;
          
          if (segHeight > 0 && segEndX > segStartX) {
            doc.rect(segStartX, segTopY, segEndX - segStartX, segHeight, 'F');
          }
        }
      }
    }
  }
  
  // Draw line AFTER area fill so it appears on top
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(1.5);
  for (let i = 0; i < points.length - 1; i++) {
    doc.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
  }
  
  // Draw data points
  points.forEach((point, idx) => {
    doc.setFillColor(color[0], color[1], color[2]);
    doc.circle(point.x, point.y, 2, 'F');
    doc.setFillColor(255, 255, 255);
    doc.circle(point.x, point.y, 1.5, 'F');
  });
  
  // X-axis labels (show every nth label to avoid crowding)
  const labelInterval = Math.max(1, Math.floor(data.length / 8));
  points.forEach((point, idx) => {
    if (idx % labelInterval === 0 || idx === points.length - 1) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const dateStr = formatDate(point.date, 'MMM dd');
      const textWidth = doc.getTextWidth(dateStr);
      doc.text(dateStr, point.x - textWidth / 2, y + padding.top + chartHeight + 8);
    }
  });
  
  // Chart title
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(label, x + padding.left, y + 5);
  
  return y + height + 5;
};

export const generateInsightsPDF = (data: PDFGenerationData): void => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  let yPos = 20;
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  const contentWidth = pageWidth - (margin * 2);

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Insights Report', margin, yPos);
  yPos += 8;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  if (data.selectedProduct) {
    doc.text(`Product: ${data.selectedProduct.name}`, margin, yPos);
    yPos += 6;
  }
  doc.text(`Milestone: ${data.milestone === 'all-time' ? 'All Time' : data.milestone}`, margin, yPos);
  yPos += 6;
  doc.text(`Generated: ${formatDate(new Date().toISOString())}`, margin, yPos);
  yPos += 10;

  // Draw a line
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Process sections in the order specified
  data.selectedSections.forEach((sectionId) => {
    // @ts-ignore - All cases have explicit break statements, no fallthrough
    switch (sectionId) {
      case 'overview': {
    yPos = checkPageBreak(doc, yPos, 30);
    yPos = addSectionTitle(doc, 'Overview Dashboard', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['overview'], margin, yPos, contentWidth);
    
    const filteredSessions = data.sessions.filter(s => {
      if (data.milestone !== 'all-time' && s.milestone !== data.milestone) return false;
      return true;
    });
    
    const completedSessions = filteredSessions.filter(s => s.status === 'completed');
    const untestedComponents = data.getUntestedComponents();
    const componentsNeedingTestCases = data.getComponentsNeedingTestCases();
    const testerPerformance = data.getTesterPerformance();
    
    // Calculate unique bug URLs and bugged components (matching UI)
    const uniqueBugUrls = new Set<string>();
    const uniqueBugComponentPairs = new Set<string>();
    completedSessions.forEach(s => {
      if (s.bugUrls && s.bugUrls.length > 0) {
        s.bugUrls.forEach((bu: any) => {
          if (typeof bu === 'string') return;
          const url = bu.url?.trim();
          if (url) {
            uniqueBugUrls.add(url);
            const componentId = bu.componentId || bu.subDepId;
            if (componentId) {
              uniqueBugComponentPairs.add(`${url}::${componentId}`);
            }
          }
        });
      }
    });

    yPos = checkPageBreak(doc, yPos, 50);
    yPos = addSubsectionTitle(doc, 'Key Metrics', yPos);
    yPos += 2;
    
    // Card layout for key metrics - 3 columns
    const cardWidth = (pageWidth - (margin * 2) - 10) / 3;
    const cardHeight = 25;
    const cardGap = 5;
    
    // Row 1
    let cardX = margin;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Total Sessions', filteredSessions.length.toString());
    cardX += cardWidth + cardGap;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Completed Sessions', completedSessions.length.toString());
    cardX += cardWidth + cardGap;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Untested Components', untestedComponents.length.toString(), true);
    yPos += cardHeight + 8;
    
    // Row 2
    cardX = margin;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Need Test Cases', componentsNeedingTestCases.length.toString(), true);
    cardX += cardWidth + cardGap;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Total Test Plans', data.testPlans.length.toString());
    cardX += cardWidth + cardGap;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Active Testers', testerPerformance.length.toString());
    yPos += cardHeight + 8;
    
    // Row 3 - Bug metrics matching UI
    cardX = margin;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Unique Bugs', uniqueBugUrls.size.toString(), true);
    cardX += cardWidth + cardGap;
    drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Bugged Components', uniqueBugComponentPairs.size.toString(), true);
    yPos += cardHeight + 15;
        break;
      }

      case 'platform-coverage': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Platform Testing Coverage', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['platform-coverage'], margin, yPos, contentWidth);
    
    const platformStats = data.getPlatformTestingStats();
    
    const tableData = platformStats
      .filter(stat => stat && stat.platform)
      .map(stat => [
        stat.platform,
        `${stat.testedComponents ?? 0} / ${stat.totalComponents ?? 0}`,
        `${(stat.percentage ?? 0).toFixed(1)}%`,
        (stat.sessions?.length ?? 0).toString(),
      ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Platform', 'Tested / Total', 'Percentage', 'Sessions']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 35 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
      },
      margin: { left: margin, right: margin },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;
        break;
      }

      case 'feature-coverage': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Feature Coverage', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['feature-coverage'], margin, yPos, contentWidth);
    
    const featureCoverage = data.getFeatureCoverage();
    
    // Calculate platform information for each feature
    const tableData = featureCoverage
      .filter(fc => fc && fc.feature)
      .map(fc => {
        // Calculate platform coverage for this feature
        const featureComponents = data.components.filter(c => c.featureId === fc.feature?.id);
        
        // Collect all unique platforms assigned to components in this feature
        const featurePlatforms = new Set<Platform>();
        featureComponents.forEach(component => {
          if (component.platforms && component.platforms.length > 0) {
            component.platforms.forEach((platform: Platform) => {
              featurePlatforms.add(platform);
            });
          }
        });

        // Count tested platforms
        const testedPlatforms = new Set<Platform>();
        const filteredSessions = data.sessions.filter(s => {
          if (s.status !== 'completed') return false;
          if (data.milestone !== 'all-time' && s.milestone !== data.milestone) return false;
          return true;
        });

        filteredSessions.forEach(session => {
          if (session.componentStatuses && session.componentStatuses.length > 0) {
            session.componentStatuses.forEach(cs => {
              if (cs.tested && cs.componentId && cs.testedPlatforms) {
                const component = featureComponents.find(c => c.id === cs.componentId);
                if (component && component.platforms) {
                  cs.testedPlatforms.forEach((platform: Platform) => {
                    if (component.platforms?.includes(platform)) {
                      testedPlatforms.add(platform);
                    }
                  });
                }
              }
            });
          } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
            session.subDepStatuses.forEach(sds => {
              if (sds.tested && sds.subDepId && sds.testedPlatforms) {
                const component = featureComponents.find(c => c.id === sds.subDepId);
                if (component && component.platforms) {
                  sds.testedPlatforms.forEach((platform: Platform) => {
                    if (component.platforms?.includes(platform)) {
                      testedPlatforms.add(platform);
                    }
                  });
                }
              }
            });
          }
        });

        const totalPlatforms = featurePlatforms.size;
        const testedPlatformCount = testedPlatforms.size;
        const platformInfo = totalPlatforms > 0 
          ? `${testedPlatformCount}/${totalPlatforms} platforms (${totalPlatforms > 0 ? ((testedPlatformCount / totalPlatforms) * 100).toFixed(0) : '0'}%)`
          : 'N/A';

        return [
          (fc.feature?.name || 'N/A').substring(0, 35), // Truncate long names
          `${fc.testedComponents ?? 0} / ${fc.totalComponents ?? 0}`,
          `${(fc.percentage ?? 0).toFixed(1)}%`,
          platformInfo,
        ];
      });

    autoTable(doc, {
      startY: yPos,
      head: [['Feature', 'Components Tested', 'Coverage %', 'Platform Coverage']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 35 },
        2: { cellWidth: 25 },
        3: { cellWidth: 50 },
      },
      margin: { left: margin, right: margin },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;
        break;
      }

      case 'untested': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Untested Components', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['untested'], margin, yPos, contentWidth);
    
    const untested = data.getUntestedComponents();
    
    if (untested.length > 0) {
      // Group by platform for better organization
      type UntestedComponent = { feature?: { name: string }; component?: { name: string }; platform?: string; lastTested?: string };
      const byPlatform = untested.reduce((acc, uc) => {
        const platform = uc.platform || 'Unknown';
        if (!acc[platform]) acc[platform] = [];
        acc[platform].push(uc);
        return acc;
      }, {} as Record<string, UntestedComponent[]>);

      (Object.entries(byPlatform) as [string, UntestedComponent[]][]).forEach(([platform, items]) => {
        yPos = checkPageBreak(doc, yPos, 30);
        yPos = addSubsectionTitle(doc, `Platform: ${platform} (${items.length} untested)`, yPos);
        yPos += 2;
        
        const tableData = items
          .filter(uc => uc && uc.platform)
          .map(uc => [
            (uc.feature?.name || 'N/A').substring(0, 40),
            (uc.component?.name || 'N/A').substring(0, 40),
            uc.lastTested ? formatDate(uc.lastTested) : 'Never',
          ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Feature', 'Component', 'Last Tested']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 60 },
            2: { cellWidth: 40 },
          },
          margin: { left: margin, right: margin },
        });

        yPos = (doc as any).lastAutoTable.finalY + 10;
      });
      
      yPos = addText(doc, `Total untested components: ${untested.length}`, margin, yPos);
      yPos += 5;
    } else {
      yPos = addText(doc, 'No untested components found.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'needs-test-case': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Components Needing Test Cases', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['needs-test-case'], margin, yPos, contentWidth);
    
    const needingTestCases = data.getComponentsNeedingTestCases();
    
    if (needingTestCases.length > 0) {
      const tableData = needingTestCases
        .filter(comp => comp && comp.component)
        .map(comp => [
          comp.feature?.name || 'N/A',
          comp.component?.name || 'N/A',
          comp.component?.testCaseIssueUrl || 'N/A',
        ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Feature', 'Component', 'Issue URL']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 60 },
          2: { cellWidth: 60 },
        },
        margin: { left: margin, right: margin },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    } else {
      yPos = addText(doc, 'No components need test cases.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'bug-rates': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Bug Rates & Statistics', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['bug-rates'], margin, yPos, contentWidth);
    
    // Bug Rates by Platform
    const bugRatesPerPlatform = data.getBugRatesPerPlatform();
    if (bugRatesPerPlatform.length > 0) {
      yPos = addSubsectionTitle(doc, 'Bug Rates by Platform', yPos);
      yPos += 2;
      
      const tableData = bugRatesPerPlatform
        .filter(br => br && br.platform)
        .map(br => {
          const avgDurationMins = (br.avgDuration ?? 0) / 60;
          const durationDisplay = avgDurationMins >= 60 
            ? `${(avgDurationMins / 60).toFixed(1)}h` 
            : `${Math.round(avgDurationMins)}m`;
          return [
            br.platform,
            (br.totalSessions ?? 0).toString(),
            (br.totalBugs ?? 0).toString(),
            (br.sharedBugs ?? 0).toString(),
            durationDisplay,
          ];
        });

      autoTable(doc, {
        startY: yPos,
        head: [['Platform', 'Sessions', 'Bugs Found', 'Cross-Platform', 'Avg Duration']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 30, halign: 'center' },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 35, halign: 'center' },
          4: { cellWidth: 35, halign: 'center' },
        },
        margin: { left: margin, right: margin },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Bug Rates by Session Type
    if (data.getBugRatesPerSessionType) {
      const bugRatesPerSessionType = data.getBugRatesPerSessionType();
      if (bugRatesPerSessionType.length > 0) {
        yPos = checkPageBreak(doc, yPos, 40);
        yPos = addSubsectionTitle(doc, 'Bug Rates by Session Type', yPos);
        yPos += 2;
        
        const tableData = bugRatesPerSessionType
          .filter(br => br && br.sessionType)
          .map(br => {
            const avgDurationMins = (br.avgDuration ?? 0) / 60;
            const durationDisplay = avgDurationMins >= 60 
              ? `${(avgDurationMins / 60).toFixed(1)}h` 
              : `${Math.round(avgDurationMins)}m`;
            return [
              (br.sessionType || 'N/A').substring(0, 40),
              (br.totalSessions ?? 0).toString(),
              (br.totalBugs ?? 0).toString(),
              durationDisplay,
            ];
          });

        autoTable(doc, {
          startY: yPos,
          head: [['Session Type', 'Sessions', 'Bugs Found', 'Avg Duration']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 30, halign: 'center' },
            2: { cellWidth: 30, halign: 'center' },
            3: { cellWidth: 35, halign: 'center' },
          },
          margin: { left: margin, right: margin },
        });

        yPos = (doc as any).lastAutoTable.finalY + 10;
      }
    }
        break;
      }

      case 'component-health': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Component Health Analytics', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['component-health'], margin, yPos, contentWidth);
    
    const componentHealth = data.getComponentHealth();
    
    if (componentHealth.length > 0) {
      // Group by feature to match UI
      type ComponentHealthItem = {
        component: Component;
        feature: Feature;
        healthScore: number | null;
        testFrequency: number;
        bugRate: number;
        lastTested?: string;
        bugRecurrenceRate: number;
        trend: 'increasing' | 'decreasing' | 'stable';
      };
      
      type FeatureGroup = {
        feature: Feature;
        components: ComponentHealthItem[];
      };
      
      const groupedByFeature = componentHealth.reduce((acc, ch) => {
        if (!ch || !ch.feature || !ch.component) return acc;
        const featureId = ch.feature.id || 'unknown';
        if (!acc[featureId]) {
          acc[featureId] = {
            feature: ch.feature,
            components: []
          };
        }
        acc[featureId].components.push(ch as ComponentHealthItem);
        return acc;
      }, {} as Record<string, FeatureGroup>);

      const featureGroups = (Object.values(groupedByFeature) as FeatureGroup[])
        .filter((group: FeatureGroup) => group && group.feature)
        .sort((a: FeatureGroup, b: FeatureGroup) => {
          if (!a.feature || !b.feature) return 0;
          return a.feature.name.localeCompare(b.feature.name);
        });

      // Get feature bug rates for header info
      const bugRatesPerFeature = data.getBugRatesPerFeature ? data.getBugRatesPerFeature() : [];

      featureGroups.forEach(group => {
        if (!group || !group.feature) return;
        
        yPos = checkPageBreak(doc, yPos, 50);
        
        // Feature header with analytics on same row (right-aligned)
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(group.feature.name, margin, yPos);
        
        // Feature bug rate info on same row, right-aligned with padding
        const featureBugRate = bugRatesPerFeature.find((br: any) => br.feature?.id === group.feature.id);
        if (featureBugRate) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          
          // Calculate right-aligned positions
          const pageWidth = doc.internal.pageSize.width;
          const rightMargin = margin;
          const padding = 5; // Padding between text elements
          
          // Build the text strings
          const totalBugsText = `Unique Bugs: ${featureBugRate.totalBugs ?? 0}`;
          const sessionsText = `Sessions Finding Bugs: ${featureBugRate.sessionsWithBugs ?? 0}/${featureBugRate.totalSessions ?? 0}`;
          const bugRateText = `Feature Bug Rate: ${(featureBugRate.bugRate ?? 0).toFixed(1)}%`;
          
          // Calculate widths
          const totalBugsWidth = doc.getTextWidth(totalBugsText);
          const sessionsWidth = doc.getTextWidth(sessionsText);
          const bugRateWidth = doc.getTextWidth(bugRateText);
          
          // Right-align: start from right margin and work left
          let currentX = pageWidth - rightMargin - totalBugsWidth;
          doc.text(totalBugsText, currentX, yPos);
          
          currentX -= (sessionsWidth + padding);
          doc.text(sessionsText, currentX, yPos);
          
          currentX -= (bugRateWidth + padding);
          doc.text(bugRateText, currentX, yPos);
        }
        yPos += 8;

        // Components table for this feature
        const tableData = group.components
          .filter((ch: ComponentHealthItem) => ch && ch.component)
          .sort((a: ComponentHealthItem, b: ComponentHealthItem) => {
            // Sort: null scores last, then by score ascending
            if (a.healthScore === null && b.healthScore === null) {
              return (a.component?.name || '').localeCompare(b.component?.name || '');
            }
            if (a.healthScore === null) return 1;
            if (b.healthScore === null) return -1;
            return (a.healthScore ?? 0) - (b.healthScore ?? 0);
          })
          .map((ch: ComponentHealthItem) => [
            (ch.component?.name || 'N/A').substring(0, 45),
            ch.healthScore === null ? 'Not tested' : (ch.healthScore ?? 0).toString(),
            (ch.testFrequency ?? 0).toString(),
            `${(ch.bugRate ?? 0).toFixed(1)}%`,
            `${(ch.bugRecurrenceRate ?? 0).toFixed(1)}%`,
            ch.lastTested ? formatDate(ch.lastTested, 'MMM dd, yyyy') : 'Never',
            ch.trend === 'increasing' ? 'Increasing' : ch.trend === 'decreasing' ? 'Decreasing' : 'Stable',
          ]);

        if (tableData.length > 0) {
          autoTable(doc, {
            startY: yPos,
            head: [['Component', 'Health Score', 'Test Frequency', 'Bug Rate', 'Bug Recurrence', 'Last Tested', 'Bug Trend']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
            styles: { fontSize: 7, cellPadding: 1.5 },
            columnStyles: {
              0: { cellWidth: 50 },
              1: { cellWidth: 20 },
              2: { cellWidth: 20 },
              3: { cellWidth: 18 },
              4: { cellWidth: 20 },
              5: { cellWidth: 25 },
              6: { cellWidth: 20 },
            },
            margin: { left: margin, right: margin },
          });

          yPos = (doc as any).lastAutoTable.finalY + 10;
        }
      });

      yPos = addText(doc, `Total components: ${componentHealth.length}`, margin, yPos);
      yPos += 5;
    } else {
      yPos = addText(doc, 'No component health data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'bug-prone-components': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Most Bug-Prone Components', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['bug-prone-components'], margin, yPos, contentWidth);
    
    const mostBugProneComponents = data.getMostBugProneComponents();
    
    if (mostBugProneComponents.length > 0) {
      const tableData = mostBugProneComponents
        .filter(ba => ba && ba.component && ba.feature)
        .map(ba => [
          (ba.component?.name || 'N/A').substring(0, 40),
          (ba.feature?.name || 'N/A').substring(0, 30),
          (ba.totalTests ?? 0).toString(),
          (ba.bugsFound ?? 0).toString(),
          `${(ba.bugRate ?? 0).toFixed(1)}%`,
        ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Component', 'Feature', 'Tests', 'Bugs Found', 'Bug Rate']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 40 },
          2: { cellWidth: 20 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
        },
        margin: { left: margin, right: margin },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    } else {
      yPos = addText(doc, 'No bug-prone components data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'bug-prone-features': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Most Bug-Prone Features', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['bug-prone-features'], margin, yPos, contentWidth);
    
    const mostBugProneFeatures = data.getMostBugProneFeatures();
    
    if (mostBugProneFeatures.length > 0) {
      const tableData = mostBugProneFeatures
        .filter(ba => ba && ba.feature)
        .map(ba => [
          (ba.feature?.name || 'N/A').substring(0, 60),
          (ba.uniqueBugUrls ?? 0).toString(),
          (ba.buggedComponents ?? 0).toString(),
        ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Feature', 'Unique Bugs', 'Bugged Components']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
        },
        margin: { left: margin, right: margin },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    } else {
      yPos = addText(doc, 'No bug-prone features data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'bug-discovery-rate': {
    yPos = checkPageBreak(doc, yPos, 60);
    yPos = addSectionTitle(doc, 'Bug Discovery Rate Over Time', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['bug-discovery-rate'], margin, yPos, contentWidth);
    
    const bugDiscoveryRate = data.getBugDiscoveryRate();
    
    if (bugDiscoveryRate.length > 0) {
      // Calculate summary stats
      const totalBugUrls = bugDiscoveryRate.reduce((sum, bdr) => sum + (bdr.bugs ?? 0), 0);
      const totalBuggedComponents = bugDiscoveryRate.reduce((sum, bdr) => sum + (bdr.buggedComponents ?? 0), 0);
      const avgBugsPerDay = bugDiscoveryRate.length > 0 ? (totalBugUrls / bugDiscoveryRate.length) : 0;
      const maxBugs = Math.max(...bugDiscoveryRate.map(bdr => bdr.bugs ?? 0), 0);
      const maxBugsDate = bugDiscoveryRate.find(bdr => (bdr.bugs ?? 0) === maxBugs)?.date;
      
      // Summary cards - 4 cards now
      yPos = checkPageBreak(doc, yPos, 30);
      const cardWidth = (contentWidth - 15) / 4;
      const cardHeight = 20;
      let cardX = margin;
      
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Unique Bugs', totalBugUrls.toString(), true);
      cardX += cardWidth + 5;
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Bugged Components', totalBuggedComponents.toString(), true);
      cardX += cardWidth + 5;
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Avg Bugs/Day', avgBugsPerDay.toFixed(1));
      cardX += cardWidth + 5;
      // Peak Day card - date on same line as value
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Peak Day', maxBugs.toString(), true);
      if (maxBugsDate) {
        const dateText = formatDate(maxBugsDate, 'MMM dd, yyyy');
        const valueText = maxBugs.toString();
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        const valueWidth = doc.getTextWidth(valueText);
        // Position date to the right of the value on the same line with smaller font
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(dateText, cardX + 5 + valueWidth + 5, yPos + 18);
      }
      yPos += cardHeight + 10;
      
      // Draw line chart
      yPos = checkPageBreak(doc, yPos, 80);
      const chartData = bugDiscoveryRate
        .filter(bdr => bdr && bdr.date)
        .sort((a, b) => a.date.localeCompare(b.date))
        .map(bdr => ({ date: bdr.date, value: bdr.bugs ?? 0 }));
      
      yPos = drawLineChart(doc, margin, yPos, contentWidth, 60, chartData, 'Unique Bugs Discovered Per Day', [220, 53, 69]);
    } else {
      yPos = addText(doc, 'No bug discovery rate data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'bug-tracker': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Bug Tracker', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['bug-tracker'], margin, yPos, contentWidth);
    
    const bugTrackerData = data.getBugTrackerData();
    
    if (bugTrackerData.length > 0) {
      // Summary stats
      const totalUniqueBugUrls = bugTrackerData.reduce((sum, f) => sum + f.uniqueBugUrls, 0);
      const totalBuggedComponents = bugTrackerData.reduce((sum, f) => sum + f.buggedComponentsCount, 0);
      
      // Summary cards
      yPos = checkPageBreak(doc, yPos, 30);
      const cardWidth = (contentWidth - 10) / 3;
      const cardHeight = 20;
      let cardX = margin;
      
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Features with Bugs', bugTrackerData.length.toString(), true);
      cardX += cardWidth + 5;
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Unique Bugs', totalUniqueBugUrls.toString(), true);
      cardX += cardWidth + 5;
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Bugged Components', totalBuggedComponents.toString(), true);
      yPos += cardHeight + 15;
      
      // List each feature with its components and bug URLs
      bugTrackerData.forEach((featureData, featureIdx) => {
        yPos = checkPageBreak(doc, yPos, 40);
        
        // Feature header box
        const featureBoxHeight = 10;
        doc.setFillColor(66, 139, 202);
        doc.rect(margin, yPos - 2, contentWidth, featureBoxHeight, 'F');
        
        // Feature name in white
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        const featureName = (featureData.feature?.name || 'Unknown Feature').substring(0, 45);
        doc.text(featureName, margin + 3, yPos + 4);
        
        // Feature stats on right side
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        const statsText = `${featureData.uniqueBugUrls} Unique Bugs  |  ${featureData.buggedComponentsCount} Bugged Components`;
        const statsWidth = doc.getTextWidth(statsText);
        doc.text(statsText, margin + contentWidth - statsWidth - 3, yPos + 4);
        
        yPos += featureBoxHeight + 4;
        
        // Store bug data for custom rendering
        const componentBugData = featureData.components.map(compData => ({
          compName: (compData.component?.name || 'Unknown').substring(0, 35),
          bugCount: compData.bugs.length,
          bugs: compData.bugs.map(bug => ({
            url: bug.url,
            shortUrl: bug.url.length > 55 ? bug.url.substring(0, 52) + '...' : bug.url,
            platforms: bug.platforms.length > 0 ? `[${bug.platforms.join(', ')}]` : ''
          }))
        }));
        
        // Create table data with placeholder for bug column
        const componentTableData = componentBugData.map(comp => [
          comp.compName,
          comp.bugCount.toString(),
          '' // Placeholder - we'll render bugs manually
        ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Component', 'Bugs', 'Bug URLs & Platforms']],
          body: componentTableData,
          theme: 'striped',
          headStyles: { 
            fillColor: [100, 100, 100], 
            textColor: 255, 
            fontStyle: 'bold',
            fontSize: 8 
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2
          },
          columnStyles: {
            0: { cellWidth: 45, fontStyle: 'bold' },
            1: { cellWidth: 15, halign: 'center' },
            2: { cellWidth: contentWidth - 60 - 10 },
          },
          margin: { left: margin, right: margin },
          didParseCell: (hookData: any) => {
            // Calculate cell height based on number of bugs
            if (hookData.column.index === 2 && hookData.cell.section === 'body') {
              const rowIdx = hookData.row.index;
              const bugs = componentBugData[rowIdx]?.bugs || [];
              const lineHeight = 4;
              const minHeight = Math.max(bugs.length * lineHeight + 4, 8);
              hookData.cell.styles.minCellHeight = minHeight;
            }
          },
          didDrawCell: (hookData: any) => {
            // Custom render bug URLs column with proper colors and links
            if (hookData.column.index === 2 && hookData.cell.section === 'body') {
              const rowIdx = hookData.row.index;
              const bugs = componentBugData[rowIdx]?.bugs || [];
              const cellX = hookData.cell.x + 2;
              let cellY = hookData.cell.y + 4;
              const lineHeight = 4;
              
              bugs.forEach((bug: { url: string; shortUrl: string; platforms: string }) => {
                // Draw bullet
                doc.setFontSize(7);
                doc.setTextColor(0, 0, 0);
                doc.text('•', cellX, cellY);
                
                // Draw URL in blue (clickable)
                doc.setTextColor(0, 102, 204);
                const urlX = cellX + 3;
                doc.textWithLink(bug.shortUrl, urlX, cellY, { url: bug.url });
                
                // Draw platforms in gray after URL
                if (bug.platforms) {
                  const urlWidth = doc.getTextWidth(bug.shortUrl);
                  doc.setTextColor(100, 100, 100);
                  doc.setFontSize(6);
                  doc.text(' ' + bug.platforms, urlX + urlWidth, cellY);
                  doc.setFontSize(7);
                }
                
                cellY += lineHeight;
              });
            }
          }
        });
        
        yPos = (doc as any).lastAutoTable.finalY + 8;
        
        // Add separator line between features (except last)
        if (featureIdx < bugTrackerData.length - 1) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.line(margin, yPos, margin + contentWidth, yPos);
          yPos += 8;
        }
      });
    } else {
      yPos = addText(doc, 'No bugs found for the selected milestone.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'trends': {
    yPos = checkPageBreak(doc, yPos, 60);
    yPos = addSectionTitle(doc, 'Testing Trends', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['trends'], margin, yPos, contentWidth);
    
    const trends = data.getTestingTrends();
    
    if (trends.length > 0) {
      // Group by date for summary
      const trendsByDate = new Map<string, { total: number; platforms: Set<Platform>; platformCounts: Map<Platform, number> }>();
      trends.forEach(trend => {
        if (!trendsByDate.has(trend.date)) {
          trendsByDate.set(trend.date, { total: 0, platforms: new Set(), platformCounts: new Map() });
        }
        const entry = trendsByDate.get(trend.date)!;
        entry.total += trend.count;
        entry.platforms.add(trend.platform);
        entry.platformCounts.set(trend.platform, (entry.platformCounts.get(trend.platform) || 0) + trend.count);
      });

      // Calculate summary statistics
      const totalSessions = trends.reduce((sum, t) => sum + t.count, 0);
      const uniqueDates = trendsByDate.size;
      const avgSessionsPerDay = uniqueDates > 0 ? (totalSessions / uniqueDates) : 0;
      const peakDay = Array.from(trendsByDate.entries())
        .sort((a, b) => b[1].total - a[1].total)[0];
      const platformTotals = new Map<Platform, number>();
      trends.forEach(t => {
        platformTotals.set(t.platform, (platformTotals.get(t.platform) || 0) + t.count);
      });

      // Get sorted dates for chart
      const sortedDates = Array.from(trendsByDate.keys()).sort();

      // Summary Statistics Cards - properly formatted (4 cards now, no trend)
      yPos = checkPageBreak(doc, yPos, 30);
      const cardWidth = (contentWidth - 15) / 4;
      const cardHeight = 20;
      let cardX = margin;
      
      // Total Sessions card
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Total Sessions', totalSessions.toString());
      cardX += cardWidth + 5;
      
      // Days Active card
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Days Active', uniqueDates.toString());
      cardX += cardWidth + 5;
      
      // Avg/Day card
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Avg/Day', avgSessionsPerDay.toFixed(1));
      cardX += cardWidth + 5;
      
      // Peak Day card - full date on same line as value
      const peakValue = peakDay ? peakDay[1].total.toString() : '0';
      drawCard(doc, cardX, yPos, cardWidth, cardHeight, 'Peak Day', peakValue);
      if (peakDay) {
        const dateText = formatDate(peakDay[0], 'MMM dd, yyyy');
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        const valueWidth = doc.getTextWidth(peakValue);
        // Position date to the right of the value on the same line with smaller font
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(dateText, cardX + 5 + valueWidth + 5, yPos + 18);
      }
      cardX += cardWidth + 5;
      
      yPos += cardHeight + 10;

      // Platform Summary Cards - properly formatted
      yPos = checkPageBreak(doc, yPos, 30);
      yPos = addSubsectionTitle(doc, 'Sessions by Platform', yPos);
      yPos += 2;
      
      const platformCount = Array.from(platformTotals.keys()).length;
      const platformsPerRow = Math.min(platformCount, 5);
      const platformCardWidth = (contentWidth - (platformsPerRow - 1) * 5) / platformsPerRow;
      const platformCardHeight = 18;
      cardX = margin;
      let currentPlatformIndex = 0;
      
      Array.from(platformTotals.entries())
        .sort((a, b) => b[1] - a[1])
        .forEach(([platform, count]) => {
          if (currentPlatformIndex > 0 && currentPlatformIndex % platformsPerRow === 0) {
            cardX = margin;
            yPos += platformCardHeight + 5;
            yPos = checkPageBreak(doc, yPos, platformCardHeight + 5);
          }
          
          // Draw platform card
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.roundedRect(cardX, yPos, platformCardWidth, platformCardHeight, 2, 2);
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(cardX, yPos, platformCardWidth, platformCardHeight, 2, 2, 'FD');
          
          // Platform name
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text(platform, cardX + 5, yPos + 6);
          
          // Count value
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const countText = count.toString();
          const countTextWidth = doc.getTextWidth(countText);
          doc.text(countText, cardX + (platformCardWidth - countTextWidth) / 2, yPos + 13);
          
          // Percentage
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 100, 100);
          const percentage = totalSessions > 0 ? ((count / totalSessions) * 100).toFixed(1) : '0';
          const percentageText = `${percentage}% of total`;
          const percentageWidth = doc.getTextWidth(percentageText);
          doc.text(percentageText, cardX + (platformCardWidth - percentageWidth) / 2, yPos + 16.5);
          
          cardX += platformCardWidth + 5;
          currentPlatformIndex++;
        });
      yPos += platformCardHeight + 10;

      // Draw line chart
      yPos = checkPageBreak(doc, yPos, 80);
      const chartData = sortedDates.map(date => ({
        date,
        value: trendsByDate.get(date)?.total || 0
      }));
      
      yPos = drawLineChart(doc, margin, yPos, contentWidth, 60, chartData, 'Sessions Over Time', [0, 212, 255]);
    } else {
      yPos = addText(doc, 'No testing trends data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'test-plan-completion': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Test Plan Completion', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['test-plan-completion'], margin, yPos, contentWidth);
    
    const testPlanCompletion = data.getTestPlanCompletion();
    
    if (testPlanCompletion.length > 0) {
      const tableData = testPlanCompletion
        .filter(tpc => tpc && tpc.testPlan)
        .map(tpc => [
          (tpc.testPlan?.title || 'Untitled').substring(0, 50),
          `${(tpc.completionRate ?? 0).toFixed(1)}%`,
          (tpc.platformStatus?.filter(ps => ps.status !== 'not-tested').length ?? 0).toString(),
          (tpc.platformStatus?.length ?? 0).toString(),
        ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Test Plan', 'Completion Rate', 'Platforms Completed', 'Total Platforms']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 30 },
          2: { cellWidth: 35 },
          3: { cellWidth: 25 },
        },
        margin: { left: margin, right: margin },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    } else {
      yPos = addText(doc, 'No test plan completion data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'milestone-completion': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Milestone Completion', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['milestone-completion'], margin, yPos, contentWidth);
    
    const milestoneCompletion = data.getMilestoneCompletion();
    
    if (milestoneCompletion.length > 0) {
      // Display as cards matching web UI
      const cardsPerRow = 2;
      const cardWidth = (contentWidth - 10) / cardsPerRow;
      const cardHeight = 30;
      let cardX = margin;
      let cardRowY = yPos;
      let cardsInRow = 0;

      milestoneCompletion
        .filter(mc => mc && mc.milestone)
        .forEach((mc, idx) => {
          if (cardsInRow === cardsPerRow) {
            cardRowY += cardHeight + 10;
            cardX = margin;
            cardsInRow = 0;
            cardRowY = checkPageBreak(doc, cardRowY, cardHeight + 10);
          }

          // Draw card
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.roundedRect(cardX, cardRowY, cardWidth, cardHeight, 2, 2);
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(cardX, cardRowY, cardWidth, cardHeight, 2, 2, 'FD');

          // Header with milestone name and overall completion
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const milestoneName = mc.milestone || 'Unknown';
          doc.text(milestoneName, cardX + 6, cardRowY + 8);
          
          // Overall completion percentage on the right
          const overallPct = mc.overallCompletion ?? 0;
          const overallColor = overallPct >= 80 ? [40, 167, 69] : overallPct >= 50 ? [255, 193, 7] : [220, 53, 69];
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(overallColor[0], overallColor[1], overallColor[2]);
          const overallText = `${overallPct.toFixed(1)}%`;
          const overallWidth = doc.getTextWidth(overallText);
          doc.text(overallText, cardX + cardWidth - overallWidth - 6, cardRowY + 8);

          // Test Plan and Session completion stats
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text('Test Plans', cardX + 6, cardRowY + 16);
          doc.text('Sessions', cardX + 6, cardRowY + 22);
          
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const testPlanPct = mc.testPlanCompletion ?? 0;
          const sessionPct = mc.sessionCompletion ?? 0;
          doc.text(`${testPlanPct.toFixed(1)}%`, cardX + cardWidth - 50, cardRowY + 16);
          doc.text(`${sessionPct.toFixed(1)}%`, cardX + cardWidth - 50, cardRowY + 22);

          cardX += cardWidth + 10;
          cardsInRow++;
        });

      yPos = cardRowY + (cardsInRow > 0 ? cardHeight + 10 : 0);
    } else {
      yPos = addText(doc, 'No milestone completion data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'platform-map': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Platform Testing Map', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['platform-map'], margin, yPos, contentWidth);
    
    const platformMapData = data.getPlatformMap();
    
    if (Object.keys(platformMapData).length > 0) {
      // Get all platforms and session types
      const platforms = Array.from(new Set(Object.keys(platformMapData))).sort();
      const sessionTypes = new Set<string>();
      Object.values(platformMapData).forEach(sessionTypesData => {
        if (sessionTypesData) {
          Object.keys(sessionTypesData).forEach(st => sessionTypes.add(st));
        }
      });
      const sortedSessionTypes = Array.from(sessionTypes).sort();

      // Helper to format duration
      const formatDuration = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
      };

      // TRANSPOSED TABLE: Session Types as rows, Platforms as columns
      // This gives more room for session type names
      const tableData: any[][] = [];
      
      // Build rows - one per session type
      sortedSessionTypes.forEach(sessionType => {
        const row: any[] = [sessionType]; // Full session type name
        let totalTime = 0;
        
        platforms.forEach(platform => {
          const cellData = (platformMapData[platform] as any)?.[sessionType];
          const duration = cellData?.totalDuration || 0;
          totalTime += duration;
          const sessionCount = cellData?.sessionCount || 0;
          
          if (duration > 0 || sessionCount > 0) {
            row.push(`${formatDuration(duration)} (${sessionCount})`);
          } else {
            row.push('-');
          }
        });
        
        row.push(formatDuration(totalTime));
        tableData.push(row);
      });
      
      // Add totals row
      const totalsRow: any[] = ['Total'];
      platforms.forEach(platform => {
        let platformTotal = 0;
        sortedSessionTypes.forEach(sessionType => {
          const cellData = (platformMapData[platform] as any)?.[sessionType];
          platformTotal += cellData?.totalDuration || 0;
        });
        totalsRow.push(formatDuration(platformTotal));
      });
      // Grand total
      let grandTotal = 0;
      platforms.forEach(platform => {
        sortedSessionTypes.forEach(sessionType => {
          const cellData = (platformMapData[platform] as any)?.[sessionType];
          grandTotal += cellData?.totalDuration || 0;
        });
      });
      totalsRow.push(formatDuration(grandTotal));
      tableData.push(totalsRow);
      
      // Create column headers: Session Type + each platform + Total
      const headers = ['Session Type', ...platforms, 'Total'];
      
      // Calculate column widths
      const numCols = headers.length;
      const sessionTypeColWidth = 55; // Wide column for full names
      const totalColWidth = 25;
      const availableWidth = contentWidth - sessionTypeColWidth - totalColWidth;
      const platformColWidth = platforms.length > 0 
        ? Math.floor(availableWidth / platforms.length) 
        : 30;
      
      const columnStyles: any = {
        0: { cellWidth: sessionTypeColWidth, fontStyle: 'bold', halign: 'left' }
      };
      for (let i = 1; i < numCols - 1; i++) {
        columnStyles[i] = { cellWidth: platformColWidth, halign: 'center' };
      }
      columnStyles[numCols - 1] = { cellWidth: totalColWidth, halign: 'center', fontStyle: 'bold' };

      autoTable(doc, {
        startY: yPos,
        head: [headers],
        body: tableData,
        theme: 'grid',
        headStyles: { 
          fillColor: [66, 139, 202], 
          textColor: 255, 
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: 3,
          halign: 'center',
          valign: 'middle'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3,
          valign: 'middle'
        },
        columnStyles,
        margin: { left: margin, right: margin },
        didParseCell: (data: any) => {
          // Bold the totals row
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [240, 240, 240];
          }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    } else {
      yPos = addText(doc, 'No platform testing map data available. Select a specific milestone to view this data.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      case 'tester-performance': {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = addSectionTitle(doc, 'Tester Performance & Productivity', yPos);
    yPos = addSectionDescription(doc, sectionDescriptions['tester-performance'], margin, yPos, contentWidth);
    
    const testerPerformance = data.getTesterPerformance();
    
    if (testerPerformance.length > 0) {
      // Display as cards matching the UI - better spacing for all data
      const cardWidth = (contentWidth - 15) / 2;
      const cardHeight = 50;
      let cardX = margin;
      let cardRowY = yPos;
      let cardsInRow = 0;

      testerPerformance.forEach((tester, idx) => {
        if (cardsInRow === 2) {
          cardRowY += cardHeight + 10;
          cardX = margin;
          cardsInRow = 0;
          cardRowY = checkPageBreak(doc, cardRowY, cardHeight + 10);
        }

        // Draw card border
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.roundedRect(cardX, cardRowY, cardWidth, cardHeight, 2, 2);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(cardX, cardRowY, cardWidth, cardHeight, 2, 2, 'FD');

        // Card header with tester name and completion rate - completion rate top right
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        const testerName = tester.testerName || 'Unknown';
        doc.text(testerName, cardX + 7, cardRowY + 9);
        
        // Completion rate on the top right
        const completionRate = tester.completionRate ?? (tester.sessionsCreated > 0 
          ? (tester.sessionsCompleted / tester.sessionsCreated) * 100 
          : 0);
        const completionColor = completionRate >= 80 ? [40, 167, 69] : completionRate >= 50 ? [255, 193, 7] : [220, 53, 69];
        
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const completionLabel = 'Completion Rate';
        const completionLabelWidth = doc.getTextWidth(completionLabel);
        doc.text(completionLabel, cardX + cardWidth - completionLabelWidth - 6, cardRowY + 5);
        
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(completionColor[0], completionColor[1], completionColor[2]);
        const completionValue = `${completionRate.toFixed(1)}%`;
        const completionValueWidth = doc.getTextWidth(completionValue);
        doc.text(completionValue, cardX + cardWidth - completionValueWidth - 6, cardRowY + 10);

        // Stats grid - properly organized in 2 columns matching web UI
        const statBoxWidth = (cardWidth - 20) / 2;
        const statBoxHeight = 5.5;
        let statY = cardRowY + 18;
        let statX = cardX + 7;
        let statCol = 0;

        const stats = [
          { label: 'Sessions Completed', value: (tester.sessionsCompleted ?? 0).toString() },
          { label: 'Sessions Created', value: (tester.sessionsCreated ?? 0).toString() },
          { label: 'Avg Duration', value: tester.avgDuration ? `${Math.round(tester.avgDuration)}m` : 'N/A' },
          { label: 'Components Tested', value: (tester.componentsTested ?? 0).toString() },
          { label: 'Bugs Found', value: (tester.bugsFound ?? 0).toString(), isDanger: true },
          { label: 'Active Sessions', value: (tester.activeSessions ?? 0).toString() },
          { label: 'Primary Platform', value: tester.primaryPlatform || 'N/A', isBadge: true },
        ];

        stats.forEach((stat, statIdx) => {
          if (statCol === 2) {
            statCol = 0;
            statX = cardX + 7;
            statY += statBoxHeight + 1.5;
          }

          // Label
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text(stat.label, statX, statY);
          
          // Value
          if (stat.isBadge) {
            // Platform - no highlighting, just text
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(stat.value, statX, statY + 4);
          } else {
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            if (stat.isDanger) {
              doc.setTextColor(220, 53, 69);
            } else {
              doc.setTextColor(0, 0, 0);
            }
            doc.text(stat.value, statX, statY + 4);
          }

          statX += statBoxWidth + 6;
          statCol++;
        });

        cardX += cardWidth + 10;
        cardsInRow++;
      });

      yPos = cardRowY + (cardsInRow > 0 ? cardHeight + 10 : 0);
    } else {
      yPos = addText(doc, 'No tester performance data available.', margin, yPos);
      yPos += 5;
    }
        break;
      }

      default:
        // Unknown section, skip
        break;
    }
  });

  // Save the PDF
  const fileName = `insights-report-${data.selectedProduct?.name || 'report'}-${data.milestone === 'all-time' ? 'all-time' : data.milestone}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

